/**
 * Single source of truth for legal text used in:
 *  - The standalone /privacy, /terms, /sms-terms public pages
 *  - The accordion sections embedded inside the registration page
 *
 * Changing copy here automatically updates both surfaces. The
 * `version` constant is sent to the backend at registration time
 * and stamped onto the profile so we can replay which version of
 * each document the user agreed to.
 */
export const CONSENT_VERSION = '2026-04-28';
export const LAST_UPDATED = 'April 28, 2026';

/* ─── Privacy Policy ─────────────────────────────────────────── */
export const PRIVACY_POLICY = [
  {
    body: `This Privacy Policy explains how The Rippers Club ("we", "us") collects,
            uses, and protects information for members of The Rippers Club
            tennis ladder ("the Service"). By creating an account you agree
            to this policy.`,
  },
  {
    heading: 'Information We Collect',
    intro: 'We collect only the information needed to operate the ladder:',
    bullets: [
      'Your name (used to identify you on the ladder and in match results)',
      'Your email address (used for sign-in, password reset, and notifications)',
      'Your phone number (collected only if you opt in to SMS notifications)',
      'Your USTA rating (optional, self-reported)',
      'Match and challenge activity you create inside the app',
    ],
    body: 'We do not collect location data, payment information, or any sensitive identifiers.',
  },
  {
    heading: 'How We Use Your Information',
    bullets: [
      'Authenticate your account and secure your session',
      'Display your name and rank to other members of your ladder',
      'Send you notifications you have opted into (email and/or SMS)',
      'Operate the challenge, match-scoring, and ranking features',
    ],
    body: 'We do not sell your personal information. We do not use your data for advertising.',
  },
  {
    heading: 'SMS Notifications',
    body: `SMS notifications are strictly opt-in. You can review the full
            details on our SMS Terms page. You can opt out at any time by
            replying STOP to any message we send.`,
  },
  {
    heading: 'Data Storage & Security',
    body: `Your account data is stored in Supabase, a third-party database
            and authentication provider that hosts the Service. Passwords are
            hashed and never stored in plain text. Access is restricted to
            the ladder administrator and the backend service.`,
  },
  {
    heading: 'Your Rights',
    bullets: [
      'You can update or remove your phone number at any time in your profile settings.',
      'You can turn off email or SMS notifications at any time.',
      'You can request account deletion by contacting your ladder administrator.',
    ],
  },
  {
    heading: 'Changes to This Policy',
    body: `If we make material changes to this policy we will update the
            "Last updated" date and, where appropriate, notify you in-app.`,
  },
  {
    heading: 'Contact',
    body: 'Questions about this policy should be directed to the ladder administrator through the app.',
  },
];

/* ─── Terms & Conditions ─────────────────────────────────────── */
export const TERMS_AND_CONDITIONS = [
  {
    body: `These Terms & Conditions ("Terms") govern your use of The Rippers Club
            tennis ladder application ("the Service"). By signing in or
            using the Service you agree to these Terms.`,
  },
  {
    heading: 'Use of the Service',
    bullets: [
      'Account approval is required. You agree to use the Service only for the purpose of participating in the private tennis ladder you have been approved to join.',
      'You agree to provide accurate information when you create your account and to keep your login credentials confidential.',
      'You will not impersonate another member, abuse the challenge system, submit false match scores, or otherwise disrupt the ladder.',
    ],
  },
  {
    heading: 'Match Coordination',
    bullets: [
      'The Service helps members issue challenges, track scores, and manage rankings. It does not schedule matches, reserve courts, or guarantee that either player is available.',
      'You are solely responsible for contacting your opponent, agreeing on a time and place to play, and fulfilling the match within the deadlines shown in the app.',
      'The Service is not responsible for injuries, disputes, missed matches, or any other outcome of off-app scheduling and play.',
    ],
  },
  {
    heading: 'Availability & No Uptime Guarantee',
    bullets: [
      'The Service is provided on an "as is" and "as available" basis. We do not guarantee that it will be uninterrupted, error-free, or available at any particular time.',
      'Maintenance, third-party outages, or other events outside our control may cause temporary unavailability.',
    ],
  },
  {
    heading: 'Account Termination',
    body: `The ladder administrator may remove any member from the active
            ladder at any time if they determine the member is disrupting
            the Service or violating these Terms. Historical match data may
            be preserved for integrity of past rankings.`,
  },
  {
    heading: 'Limitation of Liability',
    body: `To the fullest extent permitted by law, The Rippers Club, its operators,
            and the ladder administrator will not be liable for any indirect,
            incidental, special, consequential, or punitive damages, or any
            loss of profits or revenues arising out of your use of or
            inability to use the Service. Our total liability is limited to
            the amount you have paid to use the Service, which for an
            invite-only private ladder is zero.`,
  },
  {
    heading: 'Changes to These Terms',
    body: `We may update these Terms from time to time. Continued use of the
            Service after an update constitutes your acceptance of the
            revised Terms.`,
  },
  {
    heading: 'Contact',
    body: 'Questions about these Terms should be directed to the ladder administrator through the app.',
  },
];

/* ─── SMS Terms (Twilio / TCR-aligned) ───────────────────────── */
export const SMS_TERMS = [
  {
    body: `These SMS Terms describe the text-message ("SMS") notifications
            sent by The Rippers Club. SMS consent is optional and is NOT required
            to create or use a Ladder account. By providing your phone
            number and checking the SMS-consent box during registration (or
            enabling SMS in Settings), you agree to these terms. Consent is
            not a condition of any purchase.`,
  },
  {
    heading: 'Program Description',
    body: `The Rippers Club is a private tennis-ladder management service. The SMS
            program is operated by The Rippers Club for members of The Huntington
            Club tennis ladder.`,
  },
  {
    heading: 'Message Types',
    intro: 'You may receive transactional, account-related text messages from us about:',
    bullets: [
      'Ladder challenge notifications when another member challenges you',
      'Match reminders and challenge alerts (72-hour acceptance window)',
      'Match score updates and approvals',
      'Account activity (password resets, security alerts)',
      'Ladder updates from the administrator',
    ],
    body: `We do not use SMS for marketing, promotional, or third-party
            advertising messages.`,
  },
  {
    heading: 'Opt-In',
    body: `Enrollment in the SMS program is strictly opt-in. SMS consent is
            NOT required to create or use a Ladder account. You enroll by
            entering your phone number during registration and explicitly
            checking the SMS-consent box (which is unchecked by default), or
            by toggling SMS on later in Settings. We do not enroll you
            automatically and we do not share, sell, or rent your phone
            number to third parties.`,
  },
  {
    heading: 'Message Frequency',
    body: `Message frequency varies based on your ladder activity. You may
            receive zero messages in a quiet week and several messages
            during active periods.`,
  },
  {
    heading: 'Message & Data Rates',
    body: `Message and data rates may apply depending on your mobile-carrier
            plan. The Rippers Club does not charge you for messages, but your
            mobile carrier may. Check with your carrier for details.`,
  },
  {
    heading: 'Help & Support',
    body: `Reply HELP to any text message you receive from us for assistance,
            or contact the ladder administrator through the app. We will
            respond with information about the program and how to opt out.`,
  },
  {
    heading: 'Opt-Out',
    body: `You can cancel the SMS service at any time by replying STOP to
            any text message you receive from us. After you reply STOP we
            will send a confirmation that you have been unsubscribed and
            you will not receive additional messages. If you want to
            re-enroll, you can opt in again from your profile settings.`,
  },
  {
    heading: 'Carrier Liability',
    body: `Carriers (including but not limited to AT&T, T-Mobile, Verizon,
            and other US wireless carriers) are not liable for delayed or
            undelivered messages. Messages may be delayed for various
            reasons including network congestion, technical issues, or
            handset problems.`,
  },
  {
    heading: 'Eligibility',
    body: `You must be at least 13 years old to use the Service. By
            consenting to receive SMS messages you confirm that you are
            the account holder of the mobile number provided or have the
            account holder's permission to enroll.`,
  },
  {
    heading: 'Privacy',
    body: `Information collected for the SMS program (your phone number,
            messages exchanged) is handled in accordance with our Privacy
            Policy. We will not share your phone number with third parties
            for their marketing purposes.`,
  },
  {
    heading: 'Changes to These Terms',
    body: `We may update these SMS Terms from time to time. Material changes
            will be reflected in the "Last updated" date and, where
            appropriate, communicated to enrolled users.`,
  },
  {
    heading: 'Contact',
    body: 'Questions about the SMS program should be directed to the ladder administrator through the app.',
  },
];

/* The short consent string shown next to the OPTIONAL registration checkbox.
   Must remain in sync with the public /sms-terms page. */
export const SMS_CONSENT_LABEL =
  'I agree to receive SMS notifications related to Ladder activity, including challenge notifications, match confirmations, reminders, and account alerts. Message frequency varies. Msg & data rates may apply. Reply STOP to opt out and HELP for help. Consent is not required to use The Rippers Club.';

/* FAQ block surfaced on the public /sms-terms page (A2P 10DLC reviewer-friendly). */
export const SMS_FAQ = [
  {
    q: 'Are SMS notifications required?',
    a: 'No. SMS notifications are optional and can be enabled or disabled at any time within account settings.',
  },
  {
    q: 'Do I need to consent to SMS to create an account?',
    a: 'No. SMS consent is not required to create or use a Ladder account. You may register and play without ever enabling SMS.',
  },
  {
    q: 'How do I opt out of SMS?',
    a: 'Reply STOP to any text message you receive from The Rippers Club. You can also turn SMS off at any time from Settings → Notification Preferences.',
  },
  {
    q: 'How do I get help?',
    a: 'Reply HELP to any text message you receive from The Rippers Club, or contact the ladder administrator from within the app.',
  },
  {
    q: 'How often will I receive messages?',
    a: 'Message frequency varies based on your ladder activity. You may receive zero messages in a quiet week or several during active periods.',
  },
  {
    q: 'Will I be charged for SMS messages?',
    a: 'The Rippers Club does not charge you for SMS messages. Standard message and data rates from your mobile carrier may apply.',
  },
];
